import nextSkillLibraryJson from "../content/next-skill-library.json";
import tierFiveSixLibraryJson from "../content/tier-five-six-library.json";
import { isAllowedAttackIntervalMs } from "./attack-interval";
import { isAllowedSkillRange } from "./skill-range";
import { getUndeadUnit } from "./undead-units";
import {
  validateFinanceImmediateTransitionCycles,
  validateFinanceModuleDefinition,
} from "./finance-state";
import { HIDDEN_EFFECTS } from "./hidden-effects";
import { validateLaborDefinition } from "./labor";
import type {
  AbilityMechanicDefinition,
  FinanceStateDefinition,
  LaborDefinition,
  ModuleDefinition,
} from "./phase-three-types";

interface NextSkillLibraryDocument {
  version: 1;
  modules: ModuleDefinition[];
}

const document = nextSkillLibraryJson as NextSkillLibraryDocument;
const tierFiveSixDocument =
  tierFiveSixLibraryJson as NextSkillLibraryDocument;

export const NEXT_SKILL_MODULES: ModuleDefinition[] = [
  ...document.modules,
  ...tierFiveSixDocument.modules,
].map(
  (module) => ({
    ...module,
    tags: [...module.tags],
    bonuses: { ...module.bonuses },
    combat: module.combat
      ? {
          ...module.combat,
          armorReduction: module.combat.armorReduction
            ? { ...module.combat.armorReduction }
            : undefined,
          rootEveryHits: module.combat.rootEveryHits
            ? { ...module.combat.rootEveryHits }
            : undefined,
          delayedEcho: module.combat.delayedEcho
            ? { ...module.combat.delayedEcho }
            : undefined,
        }
      : undefined,
    onAcquire: module.onAcquire ? { ...module.onAcquire } : undefined,
    experienceVault: module.experienceVault
      ? {
          ...module.experienceVault,
          evolution:
            module.experienceVault.evolution.kind === "fixed"
              ? { ...module.experienceVault.evolution }
              : {
                  ...module.experienceVault.evolution,
                  branches: module.experienceVault.evolution.branches.map(
                    (branch) => ({ ...branch }),
                  ),
                },
        }
      : undefined,
    killCharge: module.killCharge ? { ...module.killCharge } : undefined,
    financeState: module.financeState
      ? cloneFinanceState(module.financeState)
      : undefined,
    labor: module.labor ? cloneLaborDefinition(module.labor) : undefined,
    stateFamily: module.stateFamily ? { ...module.stateFamily } : undefined,
    companion: module.companion
      ? {
          ...module.companion,
          onHit: module.companion.onHit
            ? { ...module.companion.onHit }
            : undefined,
          deathBurst: module.companion.deathBurst
            ? { ...module.companion.deathBurst }
            : undefined,
          periodicHeal: module.companion.periodicHeal
            ? { ...module.companion.periodicHeal }
            : undefined,
          ownerAura: module.companion.ownerAura
            ? { ...module.companion.ownerAura }
            : undefined,
          movement: module.companion.movement
            ? { ...module.companion.movement }
            : undefined,
        }
      : undefined,
    growth: module.growth
      ? module.growth.bonuses
        ? {
            ...module.growth,
            bonuses: { ...module.growth.bonuses },
          }
        : { ...module.growth }
      : undefined,
    healing: module.healing ? { ...module.healing } : undefined,
    surfaceMining: module.surfaceMining
      ? {
          ...module.surfaceMining,
          manaPoweredSpeed: module.surfaceMining.manaPoweredSpeed
            ? { ...module.surfaceMining.manaPoweredSpeed }
            : undefined,
          devour: module.surfaceMining.devour
            ? { ...module.surfaceMining.devour }
            : undefined,
          contactAttack: module.surfaceMining.contactAttack
            ? { ...module.surfaceMining.contactAttack }
            : undefined,
          advancedDiscoveryIds: [
            ...(module.surfaceMining.advancedDiscoveryIds ?? []),
          ],
        }
      : undefined,
    conditionalEvolution: module.conditionalEvolution
      ? { ...module.conditionalEvolution }
      : undefined,
    stagedAscension: module.stagedAscension
      ? { ...module.stagedAscension }
      : undefined,
    mechanics: module.mechanics?.map((mechanic) => cloneMechanic(mechanic)),
    grantsHiddenEffectIds: [...(module.grantsHiddenEffectIds ?? [])],
  }),
);

function cloneLaborDefinition(labor: LaborDefinition): LaborDefinition {
  return { ...labor };
}

function cloneMechanic(
  mechanic: AbilityMechanicDefinition,
): AbilityMechanicDefinition {
  if (mechanic.kind === "companion_aura") {
    return { ...mechanic, targets: [...mechanic.targets] };
  }
  if (mechanic.kind === "random_skill_cast") {
    return {
      ...mechanic,
      tiers: [...mechanic.tiers],
      categories: mechanic.categories ? [...mechanic.categories] : undefined,
    };
  }
  if (mechanic.kind === "every_nth_summon_commander") {
    return {
      ...mechanic,
      auraTargetAbilityIds: [...mechanic.auraTargetAbilityIds],
    };
  }
  return { ...mechanic };
}

function cloneFinanceState(
  finance: FinanceStateDefinition,
): FinanceStateDefinition {
  return {
    ...finance,
    timeScaledIncome: finance.timeScaledIncome
      ? { ...finance.timeScaledIncome }
      : undefined,
    resourceGate: finance.resourceGate ? { ...finance.resourceGate } : undefined,
    timedOutcome:
      finance.timedOutcome?.kind === "weighted_branches"
        ? {
            ...finance.timedOutcome,
            branches: finance.timedOutcome.branches.map((branch) => ({
              ...branch,
              reward: branch.reward ? { ...branch.reward } : undefined,
            })),
          }
        : finance.timedOutcome?.kind === "stake_resolution"
          ? {
              ...finance.timedOutcome,
              dynamicSuccess: finance.timedOutcome.dynamicSuccess
                ? { ...finance.timedOutcome.dynamicSuccess }
                : undefined,
              successGrant: finance.timedOutcome.successGrant
                ? { ...finance.timedOutcome.successGrant }
                : undefined,
            }
          : undefined,
    onEnterGrant: finance.onEnterGrant
      ? {
          ...finance.onEnterGrant,
          resources: finance.onEnterGrant.resources?.map((grant) => ({
            ...grant,
          })),
        }
      : undefined,
    timedTransition: finance.timedTransition
      ? { ...finance.timedTransition }
      : undefined,
    periodicDrain: finance.periodicDrain
      ? { ...finance.periodicDrain }
      : undefined,
    periodicSpendAccumulator: finance.periodicSpendAccumulator
      ? {
          ...finance.periodicSpendAccumulator,
          thresholdReward: {
            ...finance.periodicSpendAccumulator.thresholdReward,
          },
          rareTransition: {
            ...finance.periodicSpendAccumulator.rareTransition,
          },
        }
      : undefined,
    periodicCharge: finance.periodicCharge
      ? { ...finance.periodicCharge }
      : undefined,
    onReplacementReward: finance.onReplacementReward
      ? { ...finance.onReplacementReward }
      : undefined,
    randomStake: finance.randomStake
      ? {
          ...finance.randomStake,
          targetPool: [...finance.randomStake.targetPool],
        }
      : undefined,
  };
}

export function validateNextSkillLibraryContent(): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  const byId = new Map(
    NEXT_SKILL_MODULES.map((module) => [module.id, module]),
  );
  const hiddenEffectIds = new Set(HIDDEN_EFFECTS.map((effect) => effect.id));
  const conditionalSourcesByTarget = new Map<string, ModuleDefinition[]>();
  for (const source of NEXT_SKILL_MODULES) {
    const targetId = source.conditionalEvolution?.targetModuleId;
    if (!targetId) {
      continue;
    }
    const sources = conditionalSourcesByTarget.get(targetId) ?? [];
    sources.push(source);
    conditionalSourcesByTarget.set(targetId, sources);
  }
  for (const module of NEXT_SKILL_MODULES) {
    if (ids.has(module.id)) {
      errors.push(`下一版技能ID重复: ${module.id}`);
    }
    ids.add(module.id);
    if (module.tier === 6 && module.weight !== 0) {
      errors.push(`下一版六阶技能 ${module.id} 的随机权重必须为0`);
    }
    if (module.category === "one_shot" && !module.onAcquire) {
      errors.push(`单次技能 ${module.id} 必须配置获得时效果`);
    }
    if (
      module.bonuses.manaMaxMultiplier !== undefined &&
      (!Number.isFinite(module.bonuses.manaMaxMultiplier) ||
        module.bonuses.manaMaxMultiplier <= 0)
    ) {
      errors.push(`下一版技能 ${module.id} 的最大魔力倍率必须大于0`);
    }
    if (module.onAcquire) {
      for (const [label, value] of [
        ["金币", module.onAcquire.gold],
        ["经验", module.onAcquire.experience],
        ["结晶", module.onAcquire.crystals],
        ["攻击力", module.onAcquire.damage],
        ["生命上限", module.onAcquire.maxHealth],
        ["魔力上限", module.onAcquire.maxMana],
        ["护甲", module.onAcquire.armor],
        ["普通攻击速度", module.onAcquire.attackSpeedBonus],
        ["技能伤害", module.onAcquire.abilityDamageBonus],
        ["最大魔力比例恢复", module.onAcquire.manaRegenMaxRatio],
        ["本级总经验比例", module.onAcquire.currentLevelExperienceRatio],
        ["直接等级", module.onAcquire.grantLevels],
        ["本源点", module.onAcquire.talentPoints],
      ] as const) {
        if (value !== undefined && (!Number.isFinite(value) || value < 0)) {
          errors.push(`单次技能 ${module.id} 的${label}数值无效`);
        }
      }
      if (
        module.onAcquire.grantLevels !== undefined &&
        (!Number.isInteger(module.onAcquire.grantLevels) ||
          module.onAcquire.grantLevels <= 0)
      ) {
        errors.push(`单次技能 ${module.id} 的直接等级必须为正整数`);
      }
      if (
        module.onAcquire.currentLevelExperienceRatio !== undefined &&
        module.onAcquire.currentLevelExperienceRatio <= 0
      ) {
        errors.push(`单次技能 ${module.id} 的本级总经验比例必须大于0`);
      }
      if (
        module.onAcquire.repeatableHiddenEffectId &&
        module.category !== "one_shot"
      ) {
        errors.push(`非单次技能 ${module.id} 不得授予可重复隐藏效果`);
      }
    }
    if (module.tags.includes("conditional_source")) {
      if (!module.hidden || module.weight !== 0) {
        errors.push(`条件来源技能 ${module.id} 必须隐藏且随机权重为0`);
      }
    }
    errors.push(
      ...validateFinanceModuleDefinition(module, byId, hiddenEffectIds).map(
        (message) => `理财状态 ${module.id}: ${message}`,
      ),
    );
    errors.push(
      ...validateLaborDefinition(module, hiddenEffectIds).map(
        (message) => `打工技能 ${module.id}: ${message}`,
      ),
    );
    if (module.experienceVault) {
      const vault = module.experienceVault;
      if (
        !Number.isFinite(vault.capacity) ||
        vault.capacity <= 0 ||
        !Number.isFinite(vault.replacementPayoutMultiplier) ||
        vault.replacementPayoutMultiplier <= 0 ||
        vault.suppressPayoutOnEvolution !== true
      ) {
        errors.push(`经验仓技能 ${module.id} 的容量、返还倍率或进阶抑制配置无效`);
      }
      const branches =
        vault.evolution.kind === "fixed"
          ? [
              {
                targetModuleId: vault.evolution.targetModuleId,
                weight: 100,
              },
            ]
          : vault.evolution.branches;
      const branchIds = new Set(branches.map((branch) => branch.targetModuleId));
      const totalWeight = branches.reduce((sum, branch) => sum + branch.weight, 0);
      if (
        branches.length === 0 ||
        branchIds.size !== branches.length ||
        branches.some(
          (branch) =>
            !Number.isFinite(branch.weight) ||
            branch.weight <= 0 ||
            !byId.has(branch.targetModuleId) ||
            byId.get(branch.targetModuleId)!.tier !== module.tier + 1 ||
            !byId.get(branch.targetModuleId)!.hidden ||
            byId.get(branch.targetModuleId)!.weight !== 0,
        ) ||
        (vault.evolution.kind === "weighted" &&
          Math.abs(totalWeight - 100) > 1e-9)
      ) {
        errors.push(`经验仓技能 ${module.id} 的条件进阶分支无效`);
      }
    }
    if (module.killCharge) {
      const charge = module.killCharge;
      const target = byId.get(charge.targetModuleId);
      if (
        charge.trigger !== "direct_kill" ||
        !Number.isInteger(charge.requiredCount) ||
        charge.requiredCount <= 0 ||
        charge.sameSlot !== true ||
        !target ||
        target.tier !== module.tier + 1 ||
        !target.hidden ||
        target.weight !== 0
      ) {
        errors.push(`击杀充能技能 ${module.id} 的逐槽进阶配置无效`);
      }
    }
    if (module.category === "mining") {
      const devour = module.surfaceMining?.devour;
      const contact = module.surfaceMining?.contactAttack;
      const specialTier = Boolean(
        (module.tier === 3 && module.hidden && devour) ||
          (module.tier === 6 && module.hidden && contact),
      );
      if (module.tier > 2 && !specialTier) {
        errors.push(`轻量地表采矿技能 ${module.id} 只能处于一阶或二阶`);
      }
      if (
        !module.surfaceMining ||
        ![1, 2].includes(module.surfaceMining.schemaVersion)
      ) {
        errors.push(`轻量地表采矿技能 ${module.id} 必须配置 surfaceMining v1/v2`);
      }
      if (module.evolutionTo || module.evolutionFrom) {
        errors.push(`轻量地表采矿技能 ${module.id} 不得配置五升六关系`);
      }
    } else if (module.surfaceMining) {
      errors.push(`非采矿技能 ${module.id} 不得配置 surfaceMining`);
    }
    if (module.surfaceMining) {
      const mining = module.surfaceMining;
      if (
        mining.settlementIntervalMultiplier !== undefined &&
        (!Number.isFinite(mining.settlementIntervalMultiplier) ||
          mining.settlementIntervalMultiplier <= 0 ||
          mining.settlementIntervalMultiplier > 1)
      ) {
        errors.push(`轻量地表采矿技能 ${module.id} 的结算间隔倍率无效`);
      }
      for (const [label, value] of [
        ["额外采集量", mining.extraGoldFlat],
        ["额外采集倍率", mining.extraGoldMultiplier],
        ["加法采矿速度", mining.speedBonus],
        ["最终金币加成", mining.goldYieldBonus],
        ["条件反隐倍率", mining.conditionalRevealRadiusBonus],
        ["结晶比例", mining.crystalYieldRatio],
        ["反隐范围", mining.revealRadiusAdd],
        ["侦查范围", mining.scoutingRadiusAdd],
      ] as const) {
        if (value !== undefined && (!Number.isFinite(value) || value < 0)) {
          errors.push(`轻量地表采矿技能 ${module.id} 的${label}无效`);
        }
      }
      if (
        mining.manaPoweredSpeed &&
        (!Number.isFinite(mining.manaPoweredSpeed.speedBonus) ||
          mining.manaPoweredSpeed.speedBonus <= 0 ||
          !Number.isFinite(mining.manaPoweredSpeed.manaCostPerSettlement) ||
          mining.manaPoweredSpeed.manaCostPerSettlement <= 0)
      ) {
        errors.push(`轻量地表采矿技能 ${module.id} 的魔力驱动速度无效`);
      }
      if (
        mining.devour &&
        (module.tier !== 3 ||
          !module.hidden ||
          module.weight !== 0 ||
          !module.conditionalEvolution ||
          mining.devour.channelMs <= 0 ||
          mining.devour.cooldownMs <= 0 ||
          mining.devour.goldPerRemaining <= 0 ||
          mining.devour.moveSpeedPenaltyPerSuccess <= 0 ||
          mining.devour.moveSpeedPenaltyPerSuccess >= 1)
      ) {
        errors.push(`轻量地表采矿技能 ${module.id} 的隐藏吞噬配置无效`);
      }
      if (
        mining.contactAttack &&
        (module.tier !== 6 ||
          !module.hidden ||
          module.weight !== 0 ||
          mining.contactAttack.fixedBaseMoveSpeedRatio <= 0 ||
          mining.contactAttack.fixedBaseMoveSpeedRatio >= 1 ||
          mining.contactAttack.damage <= 0 ||
          mining.contactAttack.enemyCooldownMs <= 0 ||
          mining.contactAttack.knockbackDistance < 0 ||
          mining.contactAttack.healOnKill < 0)
      ) {
        errors.push(`轻量地表采矿技能 ${module.id} 的味真族接触配置无效`);
      }
    }
    if (module.conditionalEvolution) {
      const target = byId.get(module.conditionalEvolution.targetModuleId);
      const valid =
        module.conditionalEvolution.kind === "surface_mining_devour_count"
          ? module.tier === 3 &&
            Boolean(module.surfaceMining?.devour) &&
            Number.isInteger(module.conditionalEvolution.requiredCount) &&
            module.conditionalEvolution.requiredCount > 0 &&
            module.conditionalEvolution.sameSlot === true &&
            Boolean(
              target?.tier === 6 &&
                target.hidden &&
                target.surfaceMining?.contactAttack,
            )
          : Number.isInteger(module.conditionalEvolution.requiredLevel) &&
            module.conditionalEvolution.requiredLevel > 0 &&
            module.conditionalEvolution.requiredLevel <= 20 &&
            module.conditionalEvolution.sameSlot === true &&
            Boolean(
              target &&
                target.tier === module.tier + 1 &&
                target.hidden &&
                target.weight === 0,
            );
      if (!valid) {
        errors.push(`下一版技能 ${module.id} 的条件进阶配置无效`);
      }
    }
    if (module.tier === 5) {
      const stateCanonical = module.stateFamily?.inheritsAscension
        ? byId.get(module.stateFamily.canonicalModuleId)
        : undefined;
      const ascended = module.evolutionTo
        ? byId.get(module.evolutionTo)
        : undefined;
      if (
        !stateCanonical &&
        (!ascended ||
          ascended.tier !== 6 ||
          ascended.evolutionFrom !== module.id)
      ) {
        errors.push(`下一版五阶技能 ${module.id} 缺少一一对应的六阶升格`);
      }
      const hiddenEffectCount = (module.grantsHiddenEffectIds ?? []).length;
      if (module.tags.includes("primordial") && hiddenEffectCount !== 1) {
        errors.push(`原初五阶技能 ${module.id} 必须授予且仅授予一个隐藏效果`);
      }
      if (!module.tags.includes("primordial") && hiddenEffectCount > 0) {
        errors.push(`非原初五阶技能 ${module.id} 不应授予原初符文`);
      }
    } else if (module.tier === 6) {
      const stateCanonical = module.stateFamily?.inheritsSlotLock
        ? byId.get(module.stateFamily.canonicalModuleId)
        : undefined;
      const conditionalSources = conditionalSourcesByTarget.get(module.id) ?? [];
      const conditionalSource = conditionalSources[0];
      if (conditionalSource) {
        if (
          conditionalSources.length !== 1 ||
          module.evolutionFrom ||
          conditionalSource.tier !== 3 ||
          !module.hidden ||
          !module.surfaceMining?.contactAttack
        ) {
          errors.push(`下一版六阶技能 ${module.id} 的条件进阶来源无效`);
        }
      }
      const source = module.evolutionFrom
        ? byId.get(module.evolutionFrom)
        : undefined;
      const stagedSource = Boolean(
        source?.tags.includes("multi_stage_ascension") &&
          module.tags.includes("multi_stage_ascension") &&
          source.stagedAscension?.targetModuleId === module.id,
      );
      if (
        !conditionalSource &&
        !stateCanonical &&
        !stagedSource &&
        (!source ||
          source.tier !== 5 ||
          source.evolutionTo !== module.id)
      ) {
        errors.push(`下一版六阶技能 ${module.id} 缺少一一对应的五阶来源`);
      }
      if ((module.grantsHiddenEffectIds ?? []).length > 0) {
        errors.push(`下一版六阶技能 ${module.id} 不应重复授予五阶隐藏效果`);
      }
    } else if (module.evolutionTo || module.evolutionFrom) {
      errors.push(`下一版一至四阶技能 ${module.id} 不应配置五升六关系`);
    }
    if (module.stagedAscension) {
      const target = byId.get(module.stagedAscension.targetModuleId);
      if (
        !module.tags.includes("multi_stage_ascension") ||
        !target?.tags.includes("multi_stage_ascension") ||
        target.tier !== 6 ||
        target.evolutionFrom !== module.id ||
        !Number.isFinite(module.stagedAscension.costMultiplier) ||
        module.stagedAscension.costMultiplier <= 0
      ) {
        errors.push(`下一版技能 ${module.id} 的多段六阶升格配置无效`);
      }
    }
    for (const [index, mechanic] of (module.mechanics ?? []).entries()) {
      const prefix = `下一版技能 ${module.id} 的机制${index + 1}`;
      if (
        mechanic.kind === "periodic_target_damage" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.damage <= 0 ||
          (mechanic.slotIndependent !== undefined &&
            typeof mechanic.slotIndependent !== "boolean"))
      ) {
        errors.push(`${prefix}不符合攻击间隔或范围档位规则`);
      }
      if (
        mechanic.kind === "enemy_conversion" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range))
      ) {
        errors.push(`${prefix}不符合转化间隔或范围档位规则`);
      }
      if (
        mechanic.kind === "black_hole" &&
        (!isAllowedAttackIntervalMs(mechanic.spawnIntervalMs) ||
          !isAllowedAttackIntervalMs(mechanic.tickIntervalMs) ||
          !isAllowedSkillRange(mechanic.castRange) ||
          !isAllowedSkillRange(mechanic.diameter))
      ) {
        errors.push(`${prefix}不符合黑洞间隔或范围档位规则`);
      }
      if (
        mechanic.kind === "basic_attack_cannon" &&
        !isAllowedSkillRange(mechanic.baseDiameter)
      ) {
        errors.push(`${prefix}的基础爆炸直径不符合范围档位规则`);
      }
      if (
        mechanic.kind === "pasture_effect_amplifier" &&
        mechanic.effectMultiplier < 1
      ) {
        errors.push(`${prefix}的牧灵效果倍率不得低于1`);
      }
      if (mechanic.kind === "undead_summon") {
        try {
          getUndeadUnit(mechanic.unitId);
        } catch {
          errors.push(`${prefix}引用了未知亡灵单位 ${mechanic.unitId}`);
        }
        if (
          !isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          mechanic.manaCost < 0
        ) {
          errors.push(`${prefix}的亡灵召唤间隔或魔力消耗无效`);
        }
      }
      if (
        mechanic.kind === "lich_form" &&
        (mechanic.undeadHealthDamageMultiplier < 1 ||
          mechanic.undeadSummonIntervalMultiplier <= 0 ||
          mechanic.undeadSummonIntervalMultiplier > 1 ||
          (mechanic.mode === "permanent" &&
            !mechanic.immediateBaseRespawn) ||
          (mechanic.mode === "temporary_on_death" &&
            mechanic.immediateBaseRespawn))
      ) {
        errors.push(`${prefix}的巫妖形态配置无效`);
      }
      if (
        mechanic.kind === "guard_periodic_area_damage" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.radius) ||
          mechanic.damage <= 0)
      ) {
        errors.push(`${prefix}的护卫范围伤害配置无效`);
      }
      if (
        mechanic.kind === "earth_spirit_summon" &&
        (!isAllowedSkillRange(mechanic.spawnRadius) ||
          !isAllowedSkillRange(mechanic.alertRange) ||
          mechanic.returnDelayMs <= 0)
      ) {
        errors.push(`${prefix}的土灵生成、警戒或回归配置无效`);
      }
      if (
        mechanic.kind === "follower_periodic_damage" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.damage <= 0 ||
          !Number.isInteger(mechanic.hits) ||
          mechanic.hits < 1 ||
          (mechanic.consumeEmptyTick !== undefined &&
            typeof mechanic.consumeEmptyTick !== "boolean"))
      ) {
        errors.push(`${prefix}的随从攻击配置无效`);
      }
      if (
        mechanic.kind === "follower_periodic_area_damage" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.radius) ||
          mechanic.damage <= 0 ||
          (mechanic.slowRatio !== undefined &&
            (mechanic.slowRatio <= 0 || mechanic.slowRatio > 1)) ||
          (mechanic.slowDurationMs !== undefined &&
            mechanic.slowDurationMs <= 0))
      ) {
        errors.push(`${prefix}的随从范围攻击配置无效`);
      }
      if (
        mechanic.kind === "periodic_health_extremes_damage" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.highestDamage <= 0 ||
          mechanic.lowestDamage <= 0)
      ) {
        errors.push(`${prefix}的生命极值攻击配置无效`);
      }
      if (
        mechanic.kind === "basic_attack_chain" &&
        (!Number.isInteger(mechanic.targets) ||
          mechanic.targets < 2 ||
          mechanic.initialDamage <= 0 ||
          mechanic.damageLossPerBounce < 0 ||
          !isAllowedSkillRange(mechanic.bounceRange))
      ) {
        errors.push(`${prefix}的普攻弹射配置无效`);
      }
      if (
        mechanic.kind === "movement_damage_field" &&
        (mechanic.distancePerField <= 0 ||
          !isAllowedSkillRange(mechanic.radius) ||
          !isAllowedAttackIntervalMs(mechanic.tickIntervalMs) ||
          mechanic.durationMs <= 0 ||
          mechanic.damage <= 0)
      ) {
        errors.push(`${prefix}的移动伤害场配置无效`);
      }
      if (
        mechanic.kind === "guard_charge" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.acquireRange) ||
          !isAllowedSkillRange(mechanic.pathWidth) ||
          mechanic.damage <= 0)
      ) {
        errors.push(`${prefix}的护卫冲锋配置无效`);
      }
      if (
        mechanic.kind === "periodic_self_area_damage" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.radius) ||
          mechanic.damage <= 0 ||
          (mechanic.healOnPositiveHit !== undefined &&
            mechanic.healOnPositiveHit <= 0) ||
          (mechanic.slotIndependent !== undefined &&
            typeof mechanic.slotIndependent !== "boolean") ||
          (mechanic.slowRatio !== undefined &&
            (mechanic.slowRatio <= 0 || mechanic.slowRatio > 1)) ||
          (mechanic.slowDurationMs !== undefined &&
            mechanic.slowDurationMs <= 0))
      ) {
        errors.push(`${prefix}的自身范围伤害配置无效`);
      }
      if (
        mechanic.kind === "periodic_normal_execute_reward" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.rewardMultiplier < 1)
      ) {
        errors.push(`${prefix}的普通敌人处决或资源倍率配置无效`);
      }
      if (
        mechanic.kind === "periodic_forced_aggro" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.slowRatio <= 0 ||
          mechanic.slowRatio > 1)
      ) {
        errors.push(`${prefix}的强制仇恨或减速配置无效`);
      }
      if (
        mechanic.kind === "ability_kill_growth" &&
        (!Number.isInteger(mechanic.killsPerSettlement) ||
          mechanic.killsPerSettlement < 1 ||
          mechanic.maxHealthAdd <= 0 ||
          mechanic.damageAdd <= 0)
      ) {
        errors.push(`${prefix}的有效击杀成长配置无效`);
      }
      if (
        mechanic.kind === "periodic_distance_extremes_damage" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.damage <= 0)
      ) {
        errors.push(`${prefix}的距离极值攻击配置无效`);
      }
      if (
        mechanic.kind === "basic_attack_health_cost" &&
        (mechanic.maxHealthCostRatio <= 0 ||
          mechanic.maxHealthCostRatio >= 1 ||
          mechanic.bonusDamage <= 0 ||
          (mechanic.healOnKillFlat ?? 0) < 0)
      ) {
        errors.push(`${prefix}的普攻扣血或击杀恢复配置无效`);
      }
      if (
        mechanic.kind === "guard_periodic_strike" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.damage <= 0 ||
          (mechanic.selfHealing ?? 0) < 0)
      ) {
        errors.push(`${prefix}的护卫周期技能配置无效`);
      }
      if (
        mechanic.kind === "guard_basic_attack_splash" &&
        (!isAllowedSkillRange(mechanic.radius) ||
          mechanic.damageMultiplier <= 0)
      ) {
        errors.push(`${prefix}的护卫普攻溅射配置无效`);
      }
      if (
        mechanic.kind === "summon_damage_aura" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.radius) ||
          mechanic.damage <= 0)
      ) {
        errors.push(`${prefix}的召唤物伤害光环配置无效`);
      }
      if (
        mechanic.kind === "every_nth_summon_commander" &&
        (!Number.isInteger(mechanic.every) ||
          mechanic.every < 2 ||
          mechanic.health <= 0 ||
          mechanic.armor < 0 ||
          mechanic.damage <= 0 ||
          !isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.auraRadius) ||
          mechanic.auraTargetAbilityIds.some((id) => !byId.has(id)))
      ) {
        errors.push(`${prefix}的军长召唤或光环配置无效`);
      }
      if (
        mechanic.kind === "gold_contract" &&
        (mechanic.drainIntervalMs <= 0 ||
          mechanic.drainValue <= 0 ||
          (mechanic.drainMode === "current_ratio" &&
            mechanic.drainValue > 1))
      ) {
        errors.push(`${prefix}的金币契约配置无效`);
      }
      if (
        mechanic.kind === "death_growth" &&
        (mechanic.maxHealthAdd <= 0 ||
          mechanic.damageAdd <= 0 ||
          mechanic.respawnDelayAddMs < 0)
      ) {
        errors.push(`${prefix}的死亡成长配置无效`);
      }
      if (
        mechanic.kind === "energy_reservoir" &&
        (mechanic.chargePerSecond <= 0 ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.triggerBelowRatio <= 0 ||
          mechanic.triggerBelowRatio >= 1 ||
          mechanic.healthPerEnergy <= 0 ||
          mechanic.manaPerEnergy <= 0)
      ) {
        errors.push(`${prefix}的瞬时能量恢复池配置无效`);
      }
      if (
        mechanic.kind === "damage_lifesteal" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          mechanic.damageRatio <= 0 ||
          mechanic.healCap <= 0)
      ) {
        errors.push(`${prefix}的伤害吸血结算配置无效`);
      }
      if (
        mechanic.kind === "cooldown_acceleration" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          mechanic.advanceMs <= 0)
      ) {
        errors.push(`${prefix}的冷却推进配置无效`);
      }
      if (
        mechanic.kind === "synergy_substitution" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          mechanic.durationMs <= 0)
      ) {
        errors.push(`${prefix}的羁绊临时替代配置无效`);
      }
    }
    const movement = module.companion?.movement;
    if (
      movement &&
      (movement.kind === "player_centered_wander"
        ? !isAllowedSkillRange(movement.wanderRadius) ||
          !isAllowedSkillRange(movement.returnTriggerRadius) ||
          !isAllowedSkillRange(movement.returnReleaseRadius) ||
          movement.returnReleaseRadius >= movement.returnTriggerRadius ||
          movement.wanderSpeedRatio < 0 ||
          movement.returnSpeedRatio < 0
        : !isAllowedSkillRange(movement.radius) ||
          movement.speedBaseRatio <= 0)
    ) {
      errors.push(`下一版技能 ${module.id} 的伙伴移动配置无效`);
    }
  }
  errors.push(...validateFinanceImmediateTransitionCycles(NEXT_SKILL_MODULES));
  return [...new Set(errors)];
}
